import { queryOptions, useQuery, useQueryClient } from "@tanstack/react-query";
import type { PropsWithChildren } from "react";
import { createContext, useContext, useEffect } from "react";
import { GenericError } from "~components/shared/ErrorPages/GenericError.tsx";
import { Spinner } from "~components/shared/Loading/Spinner/Spinner";
import { OpenAPI } from "~generated";
import { request } from "~generated/core/request.ts";
import { currentRelease } from "~lib/currentRelease.ts";
import type { BusinessConfig } from "../../../../grewe-libs/src/lib/businessConfig/businessConfigTypes.ts";

export type BusinessConfigTenant = BusinessConfig["tenants"][number];

const businessConfigContext = createContext<BusinessConfig | null>(null);

// The endpoint is public; resolving OpenAPI.TOKEN would make it wait for msal's token renewal
function getBusinessConfig() {
  return request<BusinessConfig>(
    { ...OpenAPI, TOKEN: undefined },
    { method: "GET", url: "/api/business_config" },
  );
}

export function businessConfigQueryOptions() {
  return queryOptions({
    // Keyed by release so new code never renders with a persisted config of an older release
    queryKey: ["businessConfig", currentRelease()],
    queryFn: getBusinessConfig,
  });
}

export function useBusinessConfig() {
  const context = useContext(businessConfigContext);
  if (!context) {
    throw new Error(
      "useBusinessConfig must be used within a BusinessConfigProvider",
    );
  }
  return context;
}

export function BusinessConfigProvider({ children }: PropsWithChildren) {
  const queryClient = useQueryClient();
  // Mounted queries wait for the IndexedDB restore; this public request does not have to
  useEffect(() => {
    void queryClient.prefetchQuery(businessConfigQueryOptions());
  }, [queryClient]);

  const { data: businessConfig, isPending } = useQuery(
    businessConfigQueryOptions(),
  );

  // A failed refetch keeps the persisted config; the error page only appears without any config
  if (businessConfig) {
    return (
      <businessConfigContext.Provider value={businessConfig}>
        {children}
      </businessConfigContext.Provider>
    );
  }

  if (isPending) {
    return <Spinner />;
  }

  return <GenericError />;
}

export function TestBusinessConfigProvider({
  children,
  businessConfig,
}: PropsWithChildren<{ businessConfig: BusinessConfig }>) {
  return (
    <businessConfigContext.Provider value={businessConfig}>
      {children}
    </businessConfigContext.Provider>
  );
}
